import React, { useState, useRef, useEffect } from 'react';
import { Switch, Button, Col, Drawer, Form, Input, Row, Select, Space, message, InputNumber, Divider, DatePicker } from 'antd';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { update, getById, createOrUpdate } from '../../../../apis/OdooScheduleApi';
import { createOrUpdate as createOrUpdateDetail, getByHorarioId } from '../../../../apis/OdooHorarioDetalleApi';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

const App = ({ title, id, open, finish }) => {
    const [form] = Form.useForm();
    const [ID, setID] = useState(id);
    const [modified, setModified] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    // Indica si el horario ya tiene fila en odoo_horario_detalle
    const [detalleExistente, setDetalleExistente] = useState(false);
    // Permite elegir si se captura el detalle en el mismo paso (crear o editar)
    const [agregarDetalle, setAgregarDetalle] = useState(false);

    const convertDateToISO = (date) => {
        if (!date) return null;
        if (date && typeof date === 'object' && date.format) {
            return date.format('YYYY-MM-DD');
        }
        if (typeof date === 'string' && date.includes('/')) {
            const parts = date.split('/');
            if (parts.length === 3) {
                return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
        }
        return date;
    };

    const buildDetallePayload = (fields, horarioId) => ({
        horario_id: horarioId,
        entrada: fields.entrada ? parseFloat(fields.entrada) : null,
        salida: fields.salida ? parseFloat(fields.salida) : null,
        tolerancia_entrada: fields.tolerancia_entrada ? parseFloat(fields.tolerancia_entrada) : null,
        tolerancia_salida: fields.tolerancia_salida ? parseFloat(fields.tolerancia_salida) : null,
        lista_jornada: fields.lista_jornada,
        lista_tipo_horario: fields.lista_tipo_horario,
        tiempo_extra_fijo: fields.tiempo_extra_fijo,
        maximo_tiempo_extra: fields.maximo_tiempo_extra,
        minimo_tiempo_extra: fields.minimo_tiempo_extra,
        redondeo: fields.redondeo,
        comida_activar: fields.comida_activar,
        comida_entrada: fields.comida_entrada ? parseFloat(fields.comida_entrada) : null,
        comida_salida: fields.comida_salida ? parseFloat(fields.comida_salida) : null,
        comida_tiempo: fields.comida_tiempo,
        proporcion_dia_vacacion: fields.proporcion_dia_vacacion,
        tipo_dia_lunes: fields.tipo_dia_lunes,
        tipo_dia_martes: fields.tipo_dia_martes,
        tipo_dia_miercoles: fields.tipo_dia_miercoles,
        tipo_dia_jueves: fields.tipo_dia_jueves,
        tipo_dia_viernes: fields.tipo_dia_viernes,
        tipo_dia_sabado: fields.tipo_dia_sabado,
        tipo_dia_domingo: fields.tipo_dia_domingo,
        programacion_fecha_inicial: convertDateToISO(fields.programacion_fecha_inicial),
        programacion_ritmo: fields.programacion_ritmo
    });

    const buildHorarioPayload = (fields) => ({
        codigo_corto: fields.codigo_corto,
        descripcion: fields.descripcion,
        dias_semana: fields.dias_semana,
        hora_turno: fields.hora_turno,
        hora_semana: fields.hora_semana,
        hora_periodo: fields.hora_periodo,
        activo: fields.activo
    });

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (!values) return;
        } catch (errorInfo) {
            console.log('Failed:', errorInfo);
            return;
        }

        let success = false;
        const fields = form.getFieldsValue();

        if (ID > 0) {
            const horarioPayload = buildHorarioPayload(fields);
            const response = await update(ID, horarioPayload);
            success = response.ok;

            if (response.ok && agregarDetalle) {
                const detalleResponse = await createOrUpdateDetail(buildDetallePayload(fields, ID));
                success = success && detalleResponse.ok;
            }

            if (response.ok) {
                message.success('Se actualizó correctamente el horario!');
            }
        } else {
            const response = await createOrUpdate(buildHorarioPayload(fields));
            success = response.ok;

            if (response.ok) {
                message.success('Se creó correctamente el horario!');

                if (agregarDetalle) {
                    const nuevoId = response.payload?.id;
                    if (nuevoId) {
                        const detalleResponse = await createOrUpdateDetail(
                            buildDetallePayload(fields, nuevoId)
                        );
                        if (!detalleResponse?.ok) {
                            message.warning('Horario creado, pero el detalle no se pudo guardar. Edítalo para agregarlo.');
                        }
                    } else {
                        message.warning('No se pudo obtener el id del horario para guardar el detalle.');
                    }
                }
            }
        }

        if (success) {
            setModified(true);
            onClose(true);
        }
    };

    const onClose = (reload) => {
        finish(reload ?? modified);
        setInitialValues(null);
    };

    const effectRan = useRef(false);
    useEffect(() => {
        if (!effectRan.current) {
            (async () => {
                if (ID > 0) {
                    await refreshData();
                } else {
                    setDetalleExistente(false);
                    setAgregarDetalle(false);
                    setInitialValues({
                        codigo_corto: '',
                        descripcion: '',
                        dias_semana: 0,
                        hora_turno: 0,
                        hora_semana: 0,
                        hora_periodo: null,
                        activo: true,
                        entrada: '',
                        salida: '',
                        tolerancia_entrada: '',
                        tolerancia_salida: '',
                        lista_jornada: '',
                        lista_tipo_horario: '',
                        tiempo_extra_fijo: 0,
                        maximo_tiempo_extra: 0,
                        minimo_tiempo_extra: 1,
                        redondeo: 0.9,
                        comida_activar: false,
                        comida_entrada: '',
                        comida_salida: '',
                        comida_tiempo: 0,
                        proporcion_dia_vacacion: 1,
                        tipo_dia_lunes: 1,
                        tipo_dia_martes: 1,
                        tipo_dia_miercoles: 1,
                        tipo_dia_jueves: 1,
                        tipo_dia_viernes: 1,
                        tipo_dia_sabado: 1,
                        tipo_dia_domingo: 1
                    });
                }
                setModified(false);
            })();
        }
        return () => effectRan.current = true;
    }, []);


    const refreshData = async () => {
        const response = await getById(ID);
        if (response.ok) {
            let item = response.payload;

            const detalleResponse = await getByHorarioId(ID);
            const detalle = detalleResponse.ok ? detalleResponse.payload : null;
            const tieneDetalle = Boolean(detalle);

            setDetalleExistente(tieneDetalle);
            setAgregarDetalle(tieneDetalle);

            if (tieneDetalle) {
                item = { ...item, ...detalle };
                if (item.programacion_fecha_inicial) {
                    item.programacion_fecha_inicial = dayjs(item.programacion_fecha_inicial);
                }
            }

            setInitialValues(item);
        }
    };

    return (
        <Drawer
            title={title}
            width={1000}
            placement={"right"}
            onClose={onClose}
            open={open}
            closable={true}
            extra={<Space>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleOk} type="primary">
                    Guardar
                </Button>
            </Space>}
        >
            {
                initialValues &&
                <Form
                    layout="vertical"
                    form={form}
                    requiredMark
                    autoComplete="off"
                    initialValues={initialValues}
                    onFinish={handleOk}>
                    <Row gutter={16}>
                        <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="codigo_corto"
                                label="Código Corto"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor ingresa el código corto',
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Ej: TS1, TS2, etc."
                                    maxLength={10}
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="descripcion"
                                label="Descripción"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor ingresa una descripción',
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Descripción del horario..."
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="activo"
                                label="Activo"
                                valuePropName="checked"
                            >
                                <Switch
                                    checkedChildren={<CheckOutlined />}
                                    unCheckedChildren={<CloseOutlined />}
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 8 }} sm={{ span: 8 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="dias_semana"
                                label="Días Semana"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor ingresa los días',
                                    },
                                ]}
                            >
                                <InputNumber
                                    placeholder="Días"
                                    style={{ width: '100%' }}
                                    min={0}
                                    max={7}
                                    step={1}
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 8 }} sm={{ span: 8 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="hora_turno"
                                label="Horas Turno"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor ingresa las horas',
                                    },
                                ]}
                            >
                                <InputNumber
                                    placeholder="Horas"
                                    style={{ width: '100%' }}
                                    min={0}
                                    max={24}
                                    step={0.5}
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 8 }} sm={{ span: 8 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="hora_semana"
                                label="Horas Semana"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor ingresa las horas',
                                    },
                                ]}
                            >
                                <InputNumber
                                    placeholder="Horas"
                                    style={{ width: '100%' }}
                                    min={0}
                                    step={0.5}
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 8 }} sm={{ span: 8 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="hora_periodo"
                                label="Hora Periodo"
                            >
                                <InputNumber
                                    placeholder="Horas"
                                    style={{ width: '100%' }}
                                    min={0}
                                    step={0.5}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Detalle adicional" style={{ marginBottom: 0 }}>
                                <Space align="center">
                                    <Switch
                                        checked={detalleExistente ? true : agregarDetalle}
                                        disabled={detalleExistente}
                                        onChange={(checked) => {
                                            if (detalleExistente) {
                                                message.warning('No se puede deshabilitar una vez que tiene información.');
                                                return;
                                            }
                                            setAgregarDetalle(checked);
                                        }}
                                        checkedChildren="Sí"
                                        unCheckedChildren="No"
                                    />
                                    <span style={{ color: '#666' }}>
                                        {detalleExistente
                                            ? 'Este horario ya tiene información de detalle; no se puede deshabilitar.'
                                            : '¿Deseas capturar el detalle del turno (entrada, salida, tolerancias)?'}
                                    </span>
                                </Space>
                            </Form.Item>
                        </Col>

                        {agregarDetalle && (
                            <>
                                <Col span={24}>
                                    <Divider orientation="left"></Divider>
                                </Col>

                                <Col md={{ span: 11 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                    <Row gutter={16}>
                                        <Col span={24} style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                                            Detalle Adicional por Turno
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="entrada"
                                                label="Entrada"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Ingresa la hora de entrada',
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="08:00" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="salida"
                                                label="Salida"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Ingresa la hora de salida',
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="19:00" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="tolerancia_entrada"
                                                label="Tolerancia Entrada"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Ingresa la tolerancia',
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="00:05" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="tolerancia_salida"
                                                label="Tolerancia Salida"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Ingresa la tolerancia',
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="00:05" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item
                                                name="lista_jornada"
                                                label="Jornada"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Selecciona una jornada',
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Selecciona una jornada">
                                                    <Option value={1}>Semana Completa</Option>
                                                    <Option value={2}>Semana Reducida</Option>
                                                    <Option value={3}>Menor a 8 Hrs</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item
                                                name="lista_tipo_horario"
                                                label="Tipo Horario"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Selecciona un tipo de horario',
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Selecciona un tipo de horario">
                                                    <Option value={1}>Normal</Option>
                                                    <Option value={2}>Nocturno Fecha Entrada</Option>
                                                    <Option value={3}>Nocturno Fecha Salida</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>

                                <Col md={{ span: 2 }} sm={{ span: 0 }} xs={{ span: 0 }} style={{ display: 'flex', justifyContent: 'center' }}>
                                    <Divider type="vertical" style={{ height: '100%' }} />
                                </Col>

                                <Col md={{ span: 11 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                    <Row gutter={16}>
                                        <Col span={24} style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                                            Tiempo Extra
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="tiempo_extra_fijo"
                                                label="Tiempo Extra Fijo"
                                            >
                                                <InputNumber
                                                    placeholder="0.00"
                                                    style={{ width: '100%' }}
                                                    min={0}
                                                    step={0.01}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="maximo_tiempo_extra"
                                                label="Máximo Tiempo Extra"
                                            >
                                                <InputNumber
                                                    placeholder="0.00"
                                                    style={{ width: '100%' }}
                                                    min={0}
                                                    step={0.01}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="minimo_tiempo_extra"
                                                label="Mínimo Tiempo Extra"
                                            >
                                                <InputNumber
                                                    placeholder="1.00"
                                                    style={{ width: '100%' }}
                                                    min={0}
                                                    step={0.01}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="redondeo"
                                                label="Redondeo"
                                            >
                                                <InputNumber
                                                    placeholder="0.90"
                                                    style={{ width: '100%' }}
                                                    min={0}
                                                    max={1}
                                                    step={0.01}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>

                                <Col span={24}>
                                    <Divider orientation="left"></Divider>
                                </Col>

                                <Col md={{ span: 11 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                    <Row gutter={16}>
                                        <Col span={24} style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                                            Horario Comida
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item
                                                name="comida_activar"
                                                label="Activar"
                                                valuePropName="checked"
                                            >
                                                <Switch
                                                    checkedChildren={<CheckOutlined />}
                                                    unCheckedChildren={<CloseOutlined />}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item
                                                name="comida_entrada"
                                                label="Entrada"
                                            >
                                                <Input placeholder="12:00" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item
                                                name="comida_salida"
                                                label="Salida"
                                            >
                                                <Input placeholder="13:00" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item
                                                name="comida_tiempo"
                                                label="Tiempo"
                                            >
                                                <InputNumber
                                                    placeholder="1.00"
                                                    style={{ width: '100%' }}
                                                    min={0}
                                                    step={0.25}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>

                                <Col md={{ span: 2 }} sm={{ span: 0 }} xs={{ span: 0 }} style={{ display: 'flex', justifyContent: 'center' }}>
                                    <Divider type="vertical" style={{ height: '100%' }} />
                                </Col>

                                <Col md={{ span: 11 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                    <Row gutter={16}>
                                        <Col span={24} style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                                            Proporciones
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item
                                                name="proporcion_dia_vacacion"
                                                label="Día Vacación"
                                            >
                                                <InputNumber
                                                    placeholder="1.00"
                                                    style={{ width: '100%' }}
                                                    min={0}
                                                    step={0.01}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>

                                <Col span={24}>
                                    <Divider orientation="left"></Divider>
                                </Col>

                                <Col span={24}>
                                    <Row gutter={16}>
                                        <Col md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                            <Form.Item
                                                name="tipo_dia_lunes"
                                                label="Lunes"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Selecciona el tipo de día',
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Lista Tipo día">
                                                    <Option value={1}>Hábil</Option>
                                                    <Option value={2}>Descanso1</Option>
                                                    <Option value={3}>Descanso2</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                            <Form.Item
                                                name="tipo_dia_martes"
                                                label="Martes"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Selecciona el tipo de día',
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Lista Tipo día">
                                                    <Option value={1}>Hábil</Option>
                                                    <Option value={2}>Descanso1</Option>
                                                    <Option value={3}>Descanso2</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                            <Form.Item
                                                name="tipo_dia_miercoles"
                                                label="Miércoles"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Selecciona el tipo de día',
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Lista Tipo día">
                                                    <Option value={1}>Hábil</Option>
                                                    <Option value={2}>Descanso1</Option>
                                                    <Option value={3}>Descanso2</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                            <Form.Item
                                                name="tipo_dia_jueves"
                                                label="Jueves"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Selecciona el tipo de día',
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Lista Tipo día">
                                                    <Option value={1}>Hábil</Option>
                                                    <Option value={2}>Descanso1</Option>
                                                    <Option value={3}>Descanso2</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                            <Form.Item
                                                name="tipo_dia_viernes"
                                                label="Viernes"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Selecciona el tipo de día',
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Lista Tipo día">
                                                    <Option value={1}>Hábil</Option>
                                                    <Option value={2}>Descanso1</Option>
                                                    <Option value={3}>Descanso2</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                            <Form.Item
                                                name="tipo_dia_sabado"
                                                label="Sábado"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Selecciona el tipo de día',
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Lista Tipo día">
                                                    <Option value={1}>Hábil</Option>
                                                    <Option value={2}>Descanso1</Option>
                                                    <Option value={3}>Descanso2</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                            <Form.Item
                                                name="tipo_dia_domingo"
                                                label="Domingo"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Selecciona el tipo de día',
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Lista Tipo día">
                                                    <Option value={1}>Hábil</Option>
                                                    <Option value={2}>Descanso1</Option>
                                                    <Option value={3}>Descanso2</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col span={24}>
                                    <Divider orientation="left"></Divider>
                                </Col>

                                <Col span={24}>
                                    <Row gutter={16}>
                                        <Col md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                                            <Form.Item
                                                name="programacion_fecha_inicial"
                                                label="Fecha Inicial"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Selecciona la fecha inicial',
                                                    },
                                                ]}
                                            >
                                                <DatePicker
                                                    style={{ width: '100%' }}
                                                    format="DD/MM/YYYY"
                                                    placeholder="Selecciona una fecha"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item
                                                name="programacion_ritmo"
                                                label="Ritmo"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Ingresa el ritmo',
                                                    },
                                                ]}
                                            >
                                                <TextArea
                                                    placeholder="[S.1][S.1][S.1][S.1][S.1][S.1][S.1][S.2][N.3][N.1][S.1][S.1][S.1][S.1][S.1]"
                                                    rows={4}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>
                            </>
                        )}

                    </Row>
                </Form>
            }
        </Drawer>
    );
};

export default App;